import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';

/*
 * "Feature on Home": puts the photograph open in the viewer into Home's
 * gallery (POST /home/slides, routes/home_slides.py), for those who choose
 * Home's photographs (home:curate). Nothing for anyone else, for a photograph
 * with no id (a hub row's provisional thumbnail), or for an imported
 * reference one, which the server would refuse anyway.
 *
 * Drawn on PhotoViewer's dark caption, so it is a light ghost button. What
 * happened is said on the button itself, and a refusal says why in the
 * server's terms (a public sighting, a rare species...).
 */
export default function FeatureOnHomeButton({ photo }) {
  const { t } = useTranslation('homeSlides');
  const { user } = useAuth() || {};
  const [state, setState] = useState({ phase: 'idle' });
  const photoId = photo?.photo_id;

  useEffect(() => { setState({ phase: 'idle' }); }, [photoId]);

  if (!photoId || photo.provisional || photo.source === 'reference' || !can(user, 'home:curate')) return null;

  const feature = () => {
    setState({ phase: 'busy' });
    api.post('/home/slides', { photo_id: photoId })
      .then(() => setState({ phase: 'done', text: t('featuredDone') }))
      .catch((e) => {
        const data = e?.response?.data || {};
        if (data.code === 'already_featured') {
          setState({ phase: 'done', text: t('alreadyOnHome') });
        } else if (data.code === 'photo_not_eligible') {
          const reasons = (data.reasons || []).map((r) => t(`reason.${r}`, { defaultValue: r })).join(', ');
          setState({ phase: 'refused', text: t('cannotFeature', { reasons }) });
        } else if (data.code === 'too_many_slides') {
          setState({ phase: 'refused', text: t('tooMany', { max: data.max_slides }) });
        } else {
          setState({ phase: 'refused', text: t('featureFailed', { error: data.error || e?.message || '' }) });
        }
      });
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <button type="button" onClick={feature} disabled={state.phase === 'busy' || state.phase === 'done'}
        title={t('tip.feature')} data-testid="feature-on-home" style={buttonStyle}>
        {state.phase === 'busy' ? t('featuring') : state.phase === 'done' ? state.text : t('feature')}
      </button>
      {state.phase === 'refused' && (
        <span role="alert" data-testid="feature-on-home-refused" style={{ fontSize: 12 }}>{state.text}</span>
      )}
    </span>
  );
}

const buttonStyle = {
  background: 'transparent',
  color: 'inherit',
  border: '1px solid rgba(255,255,255,0.55)',
  borderRadius: 6,
  padding: '3px 10px',
  fontSize: 12,
  cursor: 'pointer',
};
